import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import { StoreBrandingType } from "@/types/misc";
import { useTrackActions } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  CheckoutDrawer,
  ShoppingCartItem,
} from "@brandclub/common-ui";
import _isEqual from "lodash/isEqual";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import Loading from "../Loading";

import { useCheckoutDrawer } from "./useCheckoutDrawer";
import {
  addOptimisticShoppingCartItem,
  removeOptimisticShoppingCartItem,
} from "@/redux/reducers/checkout";
import { useSplitTreatments } from "@splitsoftware/splitio-react";

const BagDrawer = () => {
  const loading = useAppSelector((state) => state.checkout.loadingCarts);
  const storeDomainBrandingType = useAppSelector(
    (state) => state.appConfig?.domainConfig.storeBrandingType
  );
  const appConfig = useAppSelector(({ appConfig }) => appConfig);

  const [trackAction] = useTrackActions();

  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const cartItems = useAppSelector((state) => state.checkout.cartItems);
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);

  const { closeDrawer } = useCheckoutDrawer();

  const hasOptimisticItems = useAppSelector(
    (state) => !!state.checkout.optimisticAllCarts
  );

  const { treatments } = useSplitTreatments({
    names: ["brandclub_checkout_free_shipping_offer"],
    attributes: {
      email: userProfile?.email ?? "",
    },
  });

  const showFreeShippingOffer =
    treatments?.brandclub_checkout_free_shipping_offer?.treatment === "on";

  // const freeShippingOfferTreatment = useTreatments(
  //   ["brandclub_checkout_free_shipping_offer"],
  //   {
  //     email: userProfile?.email ?? "",
  //   }
  // )["brandclub_checkout_free_shipping_offer"].treatment;

  const dispatch = useAppDispatch();

  useEffect(() => {
    !hasOptimisticItems && dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const handleCheckout = async () => {
    trackAction(
      AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT,
      {
        cartItems: allCarts.map((cart) => ({
          items: cart.items,
          retailerId: cart.retailerId,
        })),
        retailerId: allCarts[0].retailerId,
        source: "checkout_drawer",
      },
      {
        sendImmediately: true,
      }
    );
    navigate(`/checkout?retailerId=${allCarts[0].retailerId}`);

    return Promise.resolve();
  };

  const handleViewBag = () => {
    trackAction(AnalyticsTrackingEvent.BC_CHECKOUT_OPEN_BAG_PAGE, {
      source: "web_app_organic",
    });
    closeDrawer();
    navigate("/mybag");
  };

  const onRemove = (product: ShoppingCartItem) => {
    dispatch(
      removeOptimisticShoppingCartItem({
        items: [{ ...product, quantity: -product.quantity }],
        retailerId: product.retailerId,
        totalItems: -product.quantity,
      })
    );
  };

  const handleAddQuantity = (product: ShoppingCartItem) => {
    dispatch(
      addOptimisticShoppingCartItem({
        items: [{ ...product, quantity: 1 }],
        retailerId: product.retailerId,
        totalItems: 1,
      })
    );
    trackAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
    });
  };

  const handleMinusQuantity = (product: ShoppingCartItem) => {
    if (product.quantity - 1 <= 0) {
      onRemove(product);
      return;
    }
    dispatch(
      addOptimisticShoppingCartItem({
        items: [{ ...product, quantity: -1 }],
        retailerId: product.retailerId,
        totalItems: -1,
      })
    );

    trackAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
      source: "web_app_organic",
    });
  };

  const linkTo =
    appConfig?.domainConfig?.storeBrandingType ===
      StoreBrandingType.CustomDTCStore &&
    (appConfig?.domainConfig?.customShopPageUrls?.popularProductsPage ||
      appConfig?.domainConfig?.customShopDomainName)
      ? `https://${
          appConfig?.domainConfig?.customShopPageUrls?.popularProductsPage ||
          appConfig?.domainConfig?.customShopDomainName
        }`
      : `/trending`;

  const onEmptyBagAction = () => {
    navigate(linkTo);
  };

  return (
    <CheckoutDrawer
      showFreeShippingOffer={showFreeShippingOffer}
      storeBrandingType={storeDomainBrandingType ?? ""}
      cartProducts={cartProducts}
      onEmptyBagAction={onEmptyBagAction}
      handleAddQuantity={handleAddQuantity}
      handleMinusQuantity={handleMinusQuantity}
      cartItems={cartItems}
      allCarts={allCarts as any}
      loading={loading}
      closeDrawer={closeDrawer}
      onCheckout={handleCheckout}
      onViewBag={handleViewBag}
      LoadingComponent={() => <Loading dot />}
      initiating={false}
    />
  );
};

export default BagDrawer;
